import React, { useEffect, useState, ChangeEvent, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { GridApi } from 'ag-grid-community/dist/lib/gridApi';
import {
    ACTION_ITEMS,
    DELETE_POPUP_SUMMARY_PART1,
    DELETE_POPUP_SUMMARY_PART2,
    DELETE_POPUP_TITLE
} from 'common/config';
import { DataList, MessageModal } from 'common/components';
import { Action, ButtonLabel, EntityActions } from 'common/enums';
import ConfigurationHeader from 'modules/configuration/components/configuration-header/ConfigurationHeader';
import {
    ADMIN_ROLES_MANAGEMENT_TITLE,
    getAdminRolesManagementData
} from 'modules/admin-roles-management/config';
import AdminRoleAddEdit from 'modules/admin-roles-management/components/admin-role-add-edit/AdminRoleAddEdit';
import { RoleDtoWithAcces } from 'modules/admin-roles-management/models/role-management.model';
import {
    deleteRole,
    getAllRoles
} from 'modules/admin-roles-management/utils/role-management.utils';
import useAdminRoleManagementAccess from 'modules/admin-roles-management/role-access-hooks/useAdminRoleManagementAccess';

function AdminRoleManagement() {
    const [isAddNewVisible, setIsAddNewVisible] = useState(false);
    const [searchVal, setSearchVal] = useState<string>('');
    const [isShowPopup, setShowPopup] = useState<boolean>(false);
    const [popupDetails, setPopupDetails] = useState({ title: '', text: '', type: '' });
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const [modalData, setModalData] = useState<any>('');
    const [allRolesData, setAllRolesData] = useState<RoleDtoWithAcces[]>([]);
    const gridApi = useRef<GridApi | null>(null);
    const dispatch = useDispatch();
    const { isAdminRoleActionAccessDisabled } = useAdminRoleManagementAccess();
    /**
     * This function handle serach event
     * @param event Event information object created by user on search box
     */
    const onSearchChangeHandler = (event: ChangeEvent<HTMLInputElement>) => {
        setSearchVal(event.target.value);
    };

    const getData = () => {
        if (searchVal.length > 0 && allRolesData?.length > 0) {
            return allRolesData?.filter((role) =>
                role.applicationRoleName.toLowerCase().includes(searchVal.toLowerCase())
            );
        }
        return allRolesData?.length > 0 ? allRolesData : [];
    };

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const handleAction = (action: Action, data: any, api: GridApi) => {
        if (action === Action.Delete) {
            setPopupDetails({
                title: DELETE_POPUP_TITLE,
                text: `${DELETE_POPUP_SUMMARY_PART1} this record. ${DELETE_POPUP_SUMMARY_PART2}`,
                type: EntityActions.Delete
            });
            setShowPopup(true);
            setModalData(data);
            gridApi.current = api;
        }
        if (action === Action.Edit) {
            setPopupDetails({
                title: '',
                text: '',
                type: EntityActions.Edit
            });

            setModalData(data);
            setIsAddNewVisible(true);
        }
    };

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const popupHandler = (data: any) => {
        if (popupDetails.type === EntityActions.Delete) {
            /**here we need to call delete api and if the result is successful then only we need to delete the row as below */
            /**IF SOME HOW API REJECTS TO DELETE DATA AND RETURN FALSE THEN CLOSE THIS CURRENT POP-UP USING setShowPopup(false) AND SHOW ERROR POP UP */
            handleDelete(data);
        }
    };

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const handleDelete = (data: any) => {
        const onDeleteSuccess = (isDeleted: boolean) => {
            if (isDeleted) gridApi.current?.applyTransaction({ remove: [data] });
        };
        deleteRole(data.applicationRoleId, dispatch, onDeleteSuccess);
        setShowPopup(false);
    };

    const handleClose = () => {
        setIsAddNewVisible(false);
        setModalData('');
    };

    const onSuccess = () => {
        setIsAddNewVisible(false);
        setModalData('');
        getAllRoles(dispatch, setAllRolesData);
    };

    useEffect(() => {
        getAllRoles(dispatch, setAllRolesData);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <div className="route-container">
            <ConfigurationHeader
                headerTitle={ADMIN_ROLES_MANAGEMENT_TITLE}
                buttonTitle={ACTION_ITEMS.ADD_NEW_BUTTON}
                searchVal={searchVal}
                disableButton={isAdminRoleActionAccessDisabled}
                isButtonVisible={true}
                onSearchChangeHandler={onSearchChangeHandler}
                onAddNew={() => {
                    setIsAddNewVisible(true);
                }}
            />
            <DataList
                isNoDataVisible={getData()?.length === 0}
                data={getData()}
                columnDefs={getAdminRolesManagementData(
                    handleAction,
                    isAdminRoleActionAccessDisabled
                )}
                suppressDrag={true}
            />
            <MessageModal
                headerName={popupDetails.title}
                message={popupDetails.text}
                isVisible={isShowPopup}
                isSecondaryButtonVisible={true}
                primaryButton={ButtonLabel.Ok}
                secondaryButton={ButtonLabel.Cancel}
                onPrimaryAction={() => popupHandler(modalData)}
                onSecondaryAction={() => {
                    setShowPopup(false);
                    setModalData('');
                }}
            />
            {isAddNewVisible && (
                <AdminRoleAddEdit
                    data={modalData}
                    isVisible={isAddNewVisible}
                    onClose={handleClose}
                    onSuccess={onSuccess}
                />
            )}
        </div>
    );
}

export default AdminRoleManagement;
